import React, { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { cloneDeep, set, get, last } from "lodash";
import { useForm } from "react-hook-form";

import useGetProfessions from "../../graphql/hooks/queries/useGetProfessions";
import { Profession } from "../../graphql/types";
import useTranslations from "../../hooks/useTranslations";
import {
  Badge,
  Button,
  H5,
  Modal,
  ModalActions,
  ModalBody,
  ModalHeader,
} from "../wombat-ui";
import AutoComplete from "../wombat-ui/components/Form/Input/AutoComplete";
import { TemplateQuestionFormInput } from "./TemplateQuestionForm";
import { TemplateSectionFormInput } from "./TemplateSectionForm";
import useViewer from "../../hooks/useViewer";

type JobSectionModalProps = {
  open: boolean;
  onClose: () => void;
  getValues: ReturnType<typeof useForm>["getValues"];
  setValue: ReturnType<typeof useForm>["setValue"];
  fields: any;
  insertIndex: number;
  name: string;
  reset: ReturnType<typeof useForm>["reset"];
};

const getDefaultSectionAttribute = ({
  index = 0,
  type,
}): Partial<TemplateSectionFormInput> => ({
  order: index,
  title: "",
  description: "",
  type,
});

export const getDefaultQuestionAttribute = ({
  index = 0,
  type,
  options,
  subject,
  subjectId,
  subjectType,
  referentIds,
}): Partial<TemplateQuestionFormInput> => ({
  order: index,
  label: subject?.name || "",
  options,
  reportTypes: ["FirstReport", "IntermediateReport", "FinalReport"],
  subject: subject || null,
  subjectId: subjectId || null,
  subjectType: subjectType || null,
  type,
  referentIds,
});

export const JobSectionModal: React.FC<JobSectionModalProps> = ({
  open,
  onClose,
  getValues,
  fields,
  insertIndex,
  name,
  reset,
}) => {
  const t = useTranslations();
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedSkills, setSelectedSkills] = useState({});
  const { locale } = useIntl();
  const {
    company: { questionOptionsConfigurations },
  } = useViewer();
  const questionOptionsConfiguration = questionOptionsConfigurations.find(
    (a) => a.locale === locale
  );
  
  const handleClose = useCallback(() => {
    setSelectedJob(null);
    setSelectedSkills({});
    onClose();
  }, [onClose]);

  const getLatestQuestion = useCallback(
    (startAt: number, searchWithin: Record<string, any>) => {
      let lastQuestion;
      let previousSectionIndex = startAt - 1;

      // Guard to avoid unecessary lookup
      if (previousSectionIndex < 0) return [];
      // Loop on every sections trying to find one that does have a question
      while (lastQuestion === undefined && previousSectionIndex >= 0) {
        lastQuestion = last(
          get(
            searchWithin,
            `templateAttributes.templateSectionsAttributes[${previousSectionIndex}]`
          )?.templateQuestionsAttributes
        );

        previousSectionIndex -= 1;
      }

      return lastQuestion;
    },
    []
  );

  const createSection = useCallback(() => {
    let idx = 0;
    const values = cloneDeep(getValues());
    const templateSectionsAttributesName = `${name}.templateSectionsAttributes`;
    const templateSectionsAttributes =
      get(values, templateSectionsAttributesName) || [];

    const getPreviousQuestionReferents = () => {
      let referentIds;
      // The question needs to be appended to the form
      if (insertIndex === -1)
        referentIds =
          getLatestQuestion(templateSectionsAttributes?.length, values)
            ?.referentIds || [];
      // The question is inserted
      // Pick the referents of the last question of the section that was at that position
      else
        referentIds =
          getLatestQuestion(insertIndex, values)?.referentIds || [];

      return referentIds;
    };
    
    templateSectionsAttributes
      .splice(
        insertIndex === -1 ? templateSectionsAttributes.length : insertIndex,
        0,
        {
          ...getDefaultSectionAttribute({
            type: "TemplateSectionJob",
            index: fields.length,
          }),
          title: t("templates.jobsSectionDefaultTitle", {
            profession: selectedJob.name,
          }),
          description: t("templates.jobsSectionDefaultDescription", {
            profession: selectedJob.name,
          }),
          templateQuestionsAttributes: [
            ...Object.keys(selectedSkills)
              .map((skillId) => {
                if (selectedSkills[skillId]) {
                  return getDefaultQuestionAttribute({
                    type: "TemplateQuestionSkill",
                    index: idx++,
                    options: questionOptionsConfiguration.skillQuestionOptionsConfiguration.map(
                      (e) => ({
                        value: e.value,
                        label: e.label,
                      })
                    ),
                    subject: selectedJob.skills.find((a) => a.id === skillId),
                    subjectId: skillId,
                    subjectType: "Skill",
                    referentIds: getPreviousQuestionReferents(),
                  });
                }
              })
              .filter((a) => a),
          ],
        }
      )
      .map((i, index) => ({ ...i, index }));

    set(values, templateSectionsAttributesName, templateSectionsAttributes);
    reset(values);
    handleClose();
  }, [getValues, handleClose, reset, insertIndex, name, selectedSkills]);

  const toggleSkillSelection = useCallback(
    (skill) => {
      setSelectedSkills({ ...selectedSkills, [skill]: !selectedSkills[skill] });
    },
    [selectedSkills]
  );

  const selectAllSkills = useCallback(
    (skills) => {
      const skillMap = {};
      skills?.map((skill) => (skillMap[skill.id] = true));
      setSelectedSkills(skillMap);
    },
    [selectedSkills]
  );

  const deselectAllSkills = useCallback(
    (skills) => {
      const skillMap = {};
      skills.map((skill) => (skillMap[skill.id] = false));
      setSelectedSkills(skillMap);
    },
    [selectedSkills]
  );

  return (
    <Modal isOpen={open} aria-label="Template form element creation modal">
      <ModalHeader onClose={handleClose}>
        {t("templates.createJobSection")}
        <p className="text-xs font-light text-light-900">
          {t("templates.jobSectionModalDescription")}
        </p>
      </ModalHeader>
      <ModalBody>
        <div className="pt-2">
          <AutoComplete
            size="medium"
            id={`searchJobInput`}
            listStrict
            query={{
              hook: useGetProfessions,
              accessKey: "professions",
              hookOptions: { locale },
            }}
            onChange={(e) => {
              setSelectedJob(e);
              selectAllSkills(e.skills);
            }}
            getValue={(i: Profession) => i.id}
            getLabel={(i: Profession) => i.name}
            renderItem={(i: Profession) => i.name}
            placeholder={t("templates.pickJobForSection") as string}
          />
          {selectedJob &&
            (selectedJob.skills?.length > 0 ? (
              <>
                <div className="pt-4">
                  <H5 className="text-gray-900">
                    {t("templates.relatedSkills")}
                  </H5>
                </div>
                <div className="flex py-4 space-x-4">
                  <Button
                    variant="link"
                    scale="inline"
                    onClick={() => selectAllSkills(selectedJob.skills)}
                  >
                    {t("templates.jobSkillsSelectAll")}
                  </Button>
                  <Button
                    variant="link"
                    scale="inline"
                    onClick={() => deselectAllSkills(selectedJob.skills)}
                  >
                    {t("templates.jobSkillsUnselectAll")}
                  </Button>
                </div>
                <div className="flex flex-wrap -mx-1.5">
                  {selectedJob.skills.map((skill) => (
                    <div className="px-1.5 py-1" key={skill.id}>
                      <Badge
                        variant="secondary"
                        appearance={selectedSkills[skill.id] ? "solid" : "base"}
                        role="button"
                        onClick={() => toggleSkillSelection(skill.id)}
                        id={skill.id}
                      >
                        {skill.name}
                      </Badge>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              t("templates.noSkillsFound")
            ))}
        </div>
      </ModalBody>
      <ModalActions>
        <Button
          variant="primary"
          disabled={
            !selectedJob ||
            Object.keys(selectedSkills).length === 0 ||
            Object.keys(selectedSkills).every((k) => !selectedSkills[k])
          }
          onClick={createSection}
          id="addQuestionSkills"
        >
          {t("templates.addNQuestions", {
            count: Object.keys(selectedSkills).filter((k) => selectedSkills[k])
              .length,
          })}
        </Button>
        <Button variant="subtle" onClick={handleClose}>
          {t("forms.actions.cancel")}
        </Button>
      </ModalActions>
    </Modal>
  );
};
